// Darstellung am <html>-Element: Theme (hell/dunkel/System), Dichte und Mandanten-Akzent.
// Alles wird je Gerät gemerkt. Der früheste Teil (Theme) steht zusätzlich inline in web/index.html,
// damit die Seite nicht erst hell aufblitzt.
import { useSyncExternalStore } from 'react'
import { brand } from '@shared/brand'
import { applyAccent, deriveAccent, type AccentStyle, type AccentVars } from './accent'
import { setNativeBarStyle } from './native'

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'
export type Density = 'compact' | 'regular' | 'touch'
export type DensityChoice = Density | 'auto'

export const THEME_STORAGE_KEY = `${brand.storagePrefix}.theme`
export const DENSITY_STORAGE_KEY = `${brand.storagePrefix}.density`
export const ACCENT_STORAGE_KEY = `${brand.storagePrefix}.accent`

const DARK_QUERY = '(prefers-color-scheme: dark)'
const TOUCH_QUERY = '(pointer: coarse), (max-width: 767.98px)'

const media = (query: string): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query) : null

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}
const write = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // ohne Speicher gilt die Wahl nur für diese Sitzung
  }
}

// ---- Abonnenten (für useTheme / useDensity) -------------------------------------------------------------

const listeners = new Set<() => void>()
const notify = (): void => listeners.forEach((listener) => listener())
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// ---- Theme ----------------------------------------------------------------------------------------------

export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice !== 'system') return choice
  return media(DARK_QUERY)?.matches ? 'dark' : 'light'
}

export function getThemeChoice(): ThemeChoice {
  const stored = read(THEME_STORAGE_KEY)
  return stored === 'light' || stored === 'dark' ? stored : 'system'
}

function paintTheme(theme: ResolvedTheme): void {
  document.documentElement.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', brand.themeColor[theme])
  // Merxo-App: Status- und Navigationsleiste folgen dem Theme (helle Symbole auf dunklem Grund und umgekehrt)
  setNativeBarStyle(theme === 'dark' ? 'DARK' : 'LIGHT')
}

/** Wahl merken (System = Eintrag löschen) und sofort anwenden. */
export function setTheme(choice: ThemeChoice): void {
  write(THEME_STORAGE_KEY, choice === 'system' ? null : choice)
  paintTheme(resolveTheme(choice))
  notify()
}

// ---- Dichte ---------------------------------------------------------------------------------------------

export function resolveDensity(choice: DensityChoice): Density {
  if (choice !== 'auto') return choice
  return media(TOUCH_QUERY)?.matches ? 'touch' : 'regular'
}

export function getDensityChoice(): DensityChoice {
  const stored = read(DENSITY_STORAGE_KEY)
  return stored === 'compact' || stored === 'regular' || stored === 'touch' ? stored : 'auto'
}

/** 'auto' folgt dem Gerät: grober Zeiger oder Telefonbreite → 'touch', sonst 'regular'. */
export function setDensity(choice: DensityChoice): void {
  write(DENSITY_STORAGE_KEY, choice === 'auto' ? null : choice)
  document.documentElement.dataset.density = resolveDensity(choice)
  notify()
}

// ---- Mandanten-Akzent -----------------------------------------------------------------------------------

interface StoredAccent {
  hex: string
  style?: AccentStyle
}

/**
 * Hausfarbe eines Mandanten anwenden (design-system §2.3). Ohne Angabe gilt die Marke des Produkts (Graphit).
 * `style` überschreibt die Empfehlung aus `deriveAccent`; 'bright' fällt auf 'deep' zurück, wenn es unlesbar wäre.
 * Mit `persist` merkt sich das Gerät die Farbe, damit sie beim nächsten Start ohne Aufblitzen steht.
 */
export function applyTenantAccent(
  hex: string = brand.accent.hex,
  style?: AccentStyle,
  options: { persist?: boolean; root?: HTMLElement } = {},
): AccentVars {
  const vars = deriveAccent(hex)
  applyAccent(vars, hex, style ?? vars.style, options.root)
  if (options.persist) {
    const stored: StoredAccent = style ? { hex, style } : { hex }
    write(ACCENT_STORAGE_KEY, vars.style === 'mono' ? null : JSON.stringify(stored))
  }
  return vars
}

/** Zurück zur Marke des Produkts (Graphit) – z. B. nach dem Abmelden. */
export function resetTenantAccent(root: HTMLElement = document.documentElement): void {
  applyTenantAccent(brand.accent.hex, 'mono', { root })
  root.style.removeProperty('--accent-h')
  root.style.removeProperty('--accent-c')
  root.style.removeProperty('--tenant-brand')
  write(ACCENT_STORAGE_KEY, null)
}

/** Ältere Schreibweise aus dem Gerüst; gleichbedeutend mit applyTenantAccent. */
export function setAccent(hex: string = brand.accent.hex, style?: AccentStyle): void {
  applyTenantAccent(hex, style)
}

function restoreAccent(): void {
  const raw = read(ACCENT_STORAGE_KEY)
  if (raw) {
    try {
      const stored = JSON.parse(raw) as StoredAccent
      if (typeof stored.hex === 'string' && /^#[0-9a-f]{6}$/i.test(stored.hex)) {
        applyTenantAccent(stored.hex, stored.style)
        return
      }
    } catch {
      // unlesbarer Eintrag – Produktstandard
    }
  }
  applyTenantAccent()
}

// ---- Start ----------------------------------------------------------------------------------------------

let stopWatching: (() => void) | undefined

/**
 * Einmal beim Start aufrufen (web/main.tsx): wendet Theme, Dichte und gemerkten Akzent an und folgt danach
 * den Systemeinstellungen, solange „System“ bzw. „auto“ gewählt ist. Gibt eine Aufräumfunktion zurück.
 */
export function initAppearance(): () => void {
  stopWatching?.()
  paintTheme(resolveTheme(getThemeChoice()))
  document.documentElement.dataset.density = resolveDensity(getDensityChoice())
  restoreAccent()

  const dark = media(DARK_QUERY)
  const touch = media(TOUCH_QUERY)
  const onDark = (): void => {
    if (getThemeChoice() === 'system') {
      paintTheme(resolveTheme('system'))
      notify()
    }
  }
  const onTouch = (): void => {
    if (getDensityChoice() === 'auto') {
      document.documentElement.dataset.density = resolveDensity('auto')
      notify()
    }
  }
  dark?.addEventListener('change', onDark)
  touch?.addEventListener('change', onTouch)
  stopWatching = () => {
    dark?.removeEventListener('change', onDark)
    touch?.removeEventListener('change', onTouch)
    stopWatching = undefined
  }
  return stopWatching
}

// ---- Hooks ----------------------------------------------------------------------------------------------

const themeSnapshot = (): string => `${getThemeChoice()}:${resolveTheme(getThemeChoice())}`
const densitySnapshot = (): string => `${getDensityChoice()}:${resolveDensity(getDensityChoice())}`

/** Einstellung „Darstellung“: System, Hell, Dunkel. */
export function useTheme(): { choice: ThemeChoice; resolved: ResolvedTheme; setTheme: (choice: ThemeChoice) => void } {
  const [choice, resolved] = useSyncExternalStore(subscribe, themeSnapshot, () => 'system:light').split(':') as [
    ThemeChoice,
    ResolvedTheme,
  ]
  return { choice, resolved, setTheme }
}

export function useDensity(): {
  choice: DensityChoice
  resolved: Density
  setDensity: (choice: DensityChoice) => void
} {
  const [choice, resolved] = useSyncExternalStore(subscribe, densitySnapshot, () => 'auto:regular').split(':') as [
    DensityChoice,
    Density,
  ]
  return { choice, resolved, setDensity }
}
